/**
 * In-app diagnostic recorder.
 *
 * Captures console output, network activity, and unhandled errors into a
 * bounded ring buffer so that when a user opens the "Report bug / contact
 * support" dialog we can attach the recent client-side trail — the same kind
 * of breadcrumbs Sentry/LogRocket/Datadog RUM capture, but stored locally
 * and only transmitted when the user explicitly submits a support message.
 *
 * Scope of capture (metadata only — no request/response bodies):
 *   - console.log / info / warn / error / debug
 *   - window.fetch calls (url, method, status, duration, correlation-id header)
 *   - XMLHttpRequest calls (same fields)
 *   - window 'error' + 'unhandledrejection' events
 *   - CSP 'securitypolicyviolation' events
 *
 * Deliberately NOT captured (privacy):
 *   - request or response bodies
 *   - request or response headers other than correlation/breadcrumb
 *   - cookie / storage values
 *   - input field values
 *
 * Install at bootstrap via `import "./services/diagnosticRecorder"`.
 */

type Level = "log" | "info" | "warn" | "error" | "debug";

type ConsoleEntry = {
  kind: "console";
  ts: string;
  level: Level;
  message: string;
};

type NetworkEntry = {
  kind: "network";
  ts: string;
  method: string;
  url: string;
  status: number | null;
  durationMs: number;
  ok: boolean;
  correlationId?: string;
  breadcrumb?: string;
  eventId?: string;
  error?: string;
};

type EventEntry = {
  kind: "event";
  ts: string;
  type: "error" | "unhandledrejection" | "cspviolation";
  message: string;
  source?: string;
};

export type DiagnosticEntry = ConsoleEntry | NetworkEntry | EventEntry;

const MAX_ENTRIES = 200;
const MAX_STRING_LENGTH = 2000;
const INSTALL_MARKER = "__studio1DiagnosticRecorderInstalled";

// Query parameters that carry signing material or bearer credentials. Their
// values are redacted in captured URLs so signed URLs and auth tokens don't
// leak into user-submitted bug reports.
const SENSITIVE_QUERY_PARAMS = new Set([
  "signature",
  "key-pair-id",
  "policy",
  "expires",
  "x-amz-signature",
  "x-amz-credential",
  "x-amz-security-token",
  "x-amz-date",
  "x-amz-algorithm",
  "x-amz-signedheaders",
  "x-amz-expires",
  "access_token",
  "auth_token",
  "token",
  "api_key",
  "apikey",
  "session_token",
]);

function scrubUrl(raw: string): string {
  try {
    const u = new URL(raw, window.location.origin);
    let changed = false;
    u.searchParams.forEach((_value, key) => {
      if (SENSITIVE_QUERY_PARAMS.has(key.toLowerCase())) changed = true;
    });
    if (!changed) return raw;
    const redacted = new URL(u.toString());
    redacted.searchParams.forEach((_value, key) => {
      if (SENSITIVE_QUERY_PARAMS.has(key.toLowerCase())) {
        redacted.searchParams.set(key, "[redacted]");
      }
    });
    return redacted.toString();
  } catch {
    return raw;
  }
}

const buffer: DiagnosticEntry[] = [];
let sessionStartedAt = new Date().toISOString();

function push(entry: DiagnosticEntry): void {
  try {
    if (buffer.length >= MAX_ENTRIES) buffer.shift();
    buffer.push(entry);
  } catch {
    // Recorder failure must never break the host code path.
  }
}

function safe<T>(fn: () => T): T | undefined {
  try {
    return fn();
  } catch {
    return undefined;
  }
}

function clip(value: unknown): string {
  try {
    const str =
      typeof value === "string" ? value : JSON.stringify(value, replacer);
    if (!str) return "";
    return str.length > MAX_STRING_LENGTH
      ? `${str.slice(0, MAX_STRING_LENGTH)}…[truncated]`
      : str;
  } catch {
    return "[unserialisable]";
  }
}

function replacer(_key: string, val: unknown): unknown {
  if (val instanceof Error) {
    return { name: val.name, message: val.message, stack: val.stack };
  }
  return val;
}

function formatArgs(args: unknown[]): string {
  return args.map(clip).join(" ");
}

function patchConsole(): void {
  const levels: Level[] = ["log", "info", "warn", "error", "debug"];
  for (const level of levels) {
    // eslint-disable-next-line no-console -- the recorder's job is to wrap console
    const fn = console[level];
    if (typeof fn !== "function") continue;
    const original = fn.bind(console);
    // eslint-disable-next-line no-console -- the recorder's job is to wrap console
    console[level] = (...args: unknown[]) => {
      safe(() =>
        push({
          kind: "console",
          ts: new Date().toISOString(),
          level,
          message: formatArgs(args),
        }),
      );
      original(...args);
    };
  }
}

function readCorrelationHeaders(headers: Headers | null): {
  correlationId?: string;
  breadcrumb?: string;
  eventId?: string;
} {
  if (!headers) return {};
  const out: {
    correlationId?: string;
    breadcrumb?: string;
    eventId?: string;
  } = {};
  const cid = headers.get("x-correlation-id") ?? headers.get("correlation-id");
  if (cid) out.correlationId = cid;
  const bc = headers.get("x-breadcrumb") ?? headers.get("breadcrumb");
  if (bc) out.breadcrumb = bc;
  const eid = headers.get("x-event-id") ?? headers.get("event-id");
  if (eid) out.eventId = eid;
  return out;
}

function patchFetch(): void {
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const start = performance.now();
    // Method precedence: init.method > Request.method (if input is a Request) > "GET"
    const requestMethod = safe(() =>
      typeof input !== "string" && !(input instanceof URL)
        ? input.method
        : undefined,
    );
    const method = (init?.method ?? requestMethod ?? "GET").toUpperCase();
    const url =
      safe(() => {
        const rawUrl =
          typeof input === "string"
            ? input
            : input instanceof URL
              ? input.toString()
              : input.url;
        return scrubUrl(rawUrl);
      }) ?? "";
    try {
      const res = await originalFetch(input, init);
      safe(() =>
        push({
          kind: "network",
          ts: new Date().toISOString(),
          method,
          url,
          status: res.status,
          durationMs: Math.round(performance.now() - start),
          ok: res.ok,
          ...readCorrelationHeaders(res.headers),
        }),
      );
      return res;
    } catch (err) {
      safe(() =>
        push({
          kind: "network",
          ts: new Date().toISOString(),
          method,
          url,
          status: null,
          durationMs: Math.round(performance.now() - start),
          ok: false,
          error: clip(err),
        }),
      );
      throw err;
    }
  };
}

function patchXhr(): void {
  const OriginalXHR = window.XMLHttpRequest;
  if (!OriginalXHR) return;

  const openOriginal = OriginalXHR.prototype.open;
  const sendOriginal = OriginalXHR.prototype.send;

  type Tracked = XMLHttpRequest & {
    __studio1Method?: string;
    __studio1Url?: string;
    __studio1Start?: number;
    __studio1Recorded?: boolean;
    __studio1Error?: string;
  };

  OriginalXHR.prototype.open = function (
    this: Tracked,
    method: string,
    url: string | URL,
    ...rest: unknown[]
  ) {
    this.__studio1Method = method.toUpperCase();
    this.__studio1Url = scrubUrl(
      typeof url === "string" ? url : url.toString(),
    );
    // @ts-expect-error pass-through
    return openOriginal.call(this, method, url, ...rest);
  };

  OriginalXHR.prototype.send = function (this: Tracked, ...args: unknown[]) {
    this.__studio1Start = performance.now();
    // loadend always fires after load / error / abort, so we gate recording on
    // loadend and stash the error string from the earlier event.
    this.addEventListener("error", () => {
      this.__studio1Error = "network error";
    });
    this.addEventListener("abort", () => {
      this.__studio1Error = "aborted";
    });
    this.addEventListener("loadend", () => {
      if (this.__studio1Recorded) return;
      this.__studio1Recorded = true;
      safe(() =>
        push({
          kind: "network",
          ts: new Date().toISOString(),
          method: this.__studio1Method ?? "GET",
          url: this.__studio1Url ?? "",
          status: this.status || null,
          durationMs: Math.round(
            performance.now() - (this.__studio1Start ?? performance.now()),
          ),
          ok: this.status >= 200 && this.status < 400,
          error: this.__studio1Error,
        }),
      );
    });
    // @ts-expect-error pass-through
    return sendOriginal.call(this, ...args);
  };
}

function patchWindowEvents(): void {
  window.addEventListener("error", (e) => {
    push({
      kind: "event",
      ts: new Date().toISOString(),
      type: "error",
      message: clip(e.message),
      source: e.filename ? `${e.filename}:${e.lineno}:${e.colno}` : undefined,
    });
  });

  window.addEventListener("unhandledrejection", (e) => {
    push({
      kind: "event",
      ts: new Date().toISOString(),
      type: "unhandledrejection",
      message: clip(e.reason),
    });
  });

  document.addEventListener("securitypolicyviolation", (e) => {
    push({
      kind: "event",
      ts: new Date().toISOString(),
      type: "cspviolation",
      message: `${e.violatedDirective} blocked ${e.blockedURI}`,
      source: e.sourceFile
        ? `${e.sourceFile}:${e.lineNumber}:${e.columnNumber}`
        : undefined,
    });
  });
}

function install(): void {
  const w = window as unknown as Record<string, unknown>;
  if (w[INSTALL_MARKER]) return;
  w[INSTALL_MARKER] = true;
  sessionStartedAt = new Date().toISOString();
  // Each patch is independently isolated so one failing in an exotic
  // environment doesn't prevent the others from installing and doesn't
  // prevent the app from booting.
  safe(patchConsole);
  safe(patchFetch);
  safe(patchXhr);
  safe(patchWindowEvents);
}

safe(install);

export interface DiagnosticSnapshot {
  sessionStartedAt: string;
  capturedAt: string;
  userAgent: string;
  url: string;
  entryCount: number;
  entries: DiagnosticEntry[];
}

export function getDiagnostics(): DiagnosticSnapshot {
  return {
    sessionStartedAt,
    capturedAt: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href,
    entryCount: buffer.length,
    entries: buffer.slice(),
  };
}

export function clearDiagnostics(): void {
  buffer.length = 0;
  sessionStartedAt = new Date().toISOString();
}
